import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { getUniqueId } from '../../../functions/get-unique-id';
import { ReportField } from '../../../models/api/reports/report';
import { CheckboxComponent } from '../../core/controls/checkbox.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ReportFieldDialogComponent } from './report-field-dialog.component';
import {
  ReorderableFieldListComponent,
  ReorderableFormChangedEvent,
  ReorderableFormRowDescriptor,
} from './reorderable-field-list.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';

@Component({
  selector: 'app-dyo-non-financial-fields',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CheckboxComponent,
    ButtonComponent,
    ReportFieldDialogComponent,
    ReorderableFieldListComponent,
    PageHeaderComponent,
    ActionRowComponent,
  ],
  template: `
    <app-page-header label="Non-Financial Fields" headerSize="h3" />

    <div>
      <app-action-row>
        <app-button
          left1
          emphasis="Low"
          label="Add non-financial field"
          [disabled]="!canAddMore()"
          (click)="addField()"
        />
      </app-action-row>
      <div class="tw-pb-6"></div>

      <app-reorderable-field-list
        layout="row"
        focusHost="app-dyo-non-financial-fields"
        [formArray]="fg.controls.fields"
        [mapRow]="mapNonFinancialRow"
        [showClearOnTile]="true"
        removeAriaLabel="Remove non-financial field"
        [rowExtras]="extras"
        (edit)="onEdit($event.index)"
        (clear)="clearField($event.index)"
        (changed)="onListChanged($event)"
      />

      <ng-template #extras let-fg let-item="item">
        @if (item.hasField) {
          <div>
            <app-checkbox
              label="Display only claims with changes"
              [control]="fg.controls.displayChanges"
              [showHelpText]="false"
            />
          </div>
        }
      </ng-template>

      <div class="tw-italic">
        Select up to {{ maxFields() }} non-financial fields.
      </div>
    </div>

    <app-report-field-dialog
      #fieldDialog
      (selected)="onFieldSelected($event)"
    />
  `,
})
export class DyoNonFinancialFieldsComponent implements OnInit {
  @ViewChild('fieldDialog') fieldDialog!: ReportFieldDialogComponent;

  private reports = inject(ReportsService);
  private snackbar = inject(Snackbar);
  private cdr = inject(ChangeDetectorRef);

  private currentControl: FormControl | null = null;
  private currentIndex: number | null = null;

  fg = new FormGroup({
    fields: new FormArray([]),
  });

  fieldsCount = signal(0);

  readonly mapNonFinancialRow = (
    fieldFg: FormGroup,
    _i: number,
    _n: number,
  ): ReorderableFormRowDescriptor => {
    const label = this.getFieldLabel(fieldFg);
    const hasField = !!fieldFg.controls['field'].value;
    const slotLabel = fieldFg.controls['slotLabel'].value as string;
    return {
      id: fieldFg.controls['id'].value as string,
      label: hasField ? label || slotLabel : slotLabel,
      hasField,
      showRemove: true,
      removeAriaLabel: 'Remove non-financial field',
      fixedWidth: true,
      wrapLabel: true,
    };
  };

  ngOnInit(): void {
    this.loadSavedFields();
  }

  /** Reload fields from the current report. Called by parent on client change. */
  reload(): void {
    this.loadSavedFields();
  }

  private loadSavedFields(): void {
    this.fg.controls.fields.clear();

    const report = this.reports.currentReport();
    const slots = this.nonFinancialSlots();
    const savedFields = report?.reportRequest?.nonFinancialFields ?? [];

    for (const saved of savedFields) {
      if (saved.field) {
        const slot =
          slots.find((s) => s.columnId === saved.columnId) ??
          slots[this.fields.length];
        this.fg.controls.fields.push(
          this.createFieldFormGroup(
            saved.field,
            saved.displayChanges ?? false,
            slot,
          ),
        );
      }
    }

    if (this.fields.length === 0) {
      this.fg.controls.fields.push(
        this.createFieldFormGroup(null, false, slots[0]),
      );
    }

    this.fieldsCount.set(this.fields.length);
  }

  getFieldLabel(fg: FormGroup): string {
    const field = fg.controls['field']?.value as ReportField | null;
    return field?.defaultValue ?? '';
  }

  private nonFinancialSlots(): ReportField[] {
    return (
      this.reports.currentReport()?.reportRequest?.reportFields ?? []
    ).filter((f) => f.inlineFilterType === 'COMP_NON_FINANCIAL');
  }

  private nextUnusedSlot(): ReportField | undefined {
    const used = new Set(
      this.fields.map(
        (fg) => fg.controls['slotColumnId'].value as number | null,
      ),
    );
    return this.nonFinancialSlots().find((s) => !used.has(s.columnId));
  }

  private createFieldFormGroup(
    field: ReportField | null = null,
    displayChanges = false,
    slot?: ReportField,
  ): FormGroup {
    const assigned = slot ?? this.nextUnusedSlot();
    const slotIndex = assigned
      ? this.nonFinancialSlots().findIndex(
          (s) => s.columnId === assigned.columnId,
        )
      : this.fields.length;
    const slotLabel =
      assigned?.defaultValue ??
      `Non-Financial ${String(slotIndex + 1).padStart(2, '0')}`;

    const formGroup = new FormGroup({
      id: new FormControl(getUniqueId('dyo-nonfin'), { nonNullable: true }),
      slotColumnId: new FormControl<number | null>(assigned?.columnId ?? null),
      slotLabel: new FormControl(slotLabel, { nonNullable: true }),
      field: new FormControl<ReportField | null>(field, Validators.required),
      displayChanges: new FormControl(displayChanges, { nonNullable: true }),
    });
    return formGroup;
  }

  maxFields = computed(() => this.nonFinancialSlots().length);

  canAddMore = computed(() => this.fieldsCount() < this.maxFields());

  get fields(): FormGroup[] {
    return this.fg.controls.fields.controls as FormGroup[];
  }

  addField(): void {
    if (!this.canAddMore()) return;

    this.fg.controls.fields.push(this.createFieldFormGroup());
    this.fieldsCount.set(this.fields.length);
    this.updateReport();
  }

  clearField(index: number): void {
    const fg = this.fields[index];
    if (fg) {
      fg.controls['field'].setValue(null);
      this.updateReport();
    }
  }

  onEdit(index: number): void {
    const fg = this.fields[index];
    if (!fg) return;
    this.showDialog(
      this.fieldDialog,
      fg.controls['field'] as FormControl,
      index,
    );
  }

  onListChanged(_e: ReorderableFormChangedEvent): void {
    this.fieldsCount.set(this.fields.length);
    this.cdr.markForCheck();
    this.updateReport();
  }

  showDialog(
    dialog: ReportFieldDialogComponent,
    control: FormControl,
    index: number,
  ): void {
    this.currentControl = control;
    this.currentIndex = index;

    const report = this.reports.currentReport();
    const reportId = report?.reportTemplate?.id ?? null;
    const ipColumn = this.fields[index]?.controls['slotColumnId'].value ?? null;

    dialog.show(reportId, ipColumn);
  }

  onFieldSelected(field: ReportField): void {
    if (!this.currentControl) return;

    const isDuplicate = this.fields.some(
      (fg, i) =>
        i !== this.currentIndex &&
        (fg.controls['field'].value as ReportField | null)?.columnName ===
          field.columnName,
    );

    if (isDuplicate) {
      this.snackbar.show('Error : Duplicate', 'Error');
      return;
    }

    this.currentControl.setValue(field);
    this.updateReport();
  }

  updateReport(): void {
    const nonFinancialFields = this.fields
      .map((fg) => {
        const field = fg.controls['field'].value as ReportField | null;
        const displayChanges = fg.controls['displayChanges'].value as boolean;
        const slotColumnId = fg.controls['slotColumnId'].value as number | null;

        if (!field) return null;

        return {
          columnId: slotColumnId ?? 0,
          tableAlias: field.tableAlias ?? null,
          columnName: field.columnName ?? null,
          displayChanges,
          field,
        };
      })
      .filter((f) => f !== null);

    this.reports.currentReport.update((r) => {
      if (r.reportRequest) {
        r.reportRequest.nonFinancialFields = nonFinancialFields;
      }
      return r;
    });
  }

  get invalid(): boolean {
    return this.fg.invalid;
  }
}

